
<template>
  <div class="create-simulation-page">
    <div v-if="isLoadingSettings" class="loading-overlay">
      <div class="spinner"></div>
      <p>{{ t('simulation.create.overlay.loading') }}</p>
    </div>

    <h1>{{ t('simulation.create.heading.title') }}</h1>
    <p>{{ t('simulation.create.heading.description') }}</p>

    <div
      v-if="!isLoadingSettings && loadedConfigBanner"
      class="loaded-from-history-banner"
      v-html="loadedConfigBanner"
    ></div>

    <form @submit.prevent="startSimulation">
      <section class="simulation-section">
        <h2>{{ t('simulation.create.sections.parameters.title') }}</h2>

        <div class="form-group">
          <label for="tradingCost">{{ t('simulation.create.sections.parameters.tradingCost.label') }}</label>
          <input
            type="number"
            id="tradingCost"
            v-model.number="tradingCost"
            step="0.0001"
            min="0"
            max="0.1"
            @input="validateTradingCost"
            required
          />
          <p class="help-text">{{ t('simulation.create.sections.parameters.tradingCost.helpPrimary') }}</p>
          <p class="help-text">{{ t('simulation.create.sections.parameters.tradingCost.helpSecondary') }}</p>
          <p v-if="formErrors.tradingCost" class="validation-error">{{ formErrors.tradingCost }}</p>
        </div>

        <div class="form-group date-range-group">
          <div>
            <label for="startDate">{{ t('simulation.create.sections.parameters.dateRange.startLabel') }}</label>
            <input type="date" id="startDate" v-model="startDate" @change="validateDateRange" :lang="dateInputLang" />
            <p v-if="formErrors.startDate" class="validation-error">{{ formErrors.startDate }}</p>
          </div>
          <div>
            <label for="endDate">{{ t('simulation.create.sections.parameters.dateRange.endLabel') }}</label>
            <input type="date" id="endDate" v-model="endDate" @change="validateDateRange" :lang="dateInputLang" />
            <p v-if="formErrors.endDate" class="validation-error">{{ formErrors.endDate }}</p>
          </div>
        </div>

        <div class="quick-date-buttons">
          <button
            v-for="preset in quickDateOptions"
            :key="preset.key"
            type="button"
            @click="setQuickDate(preset.key)"
            class="btn btn-outline-secondary btn-sm"
          >
            {{ preset.label }}
          </button>
        </div>

        <div class="form-group">
          <label for="stopLossPercentage">{{ t('simulation.create.sections.parameters.stopLoss.label') }}</label>
          <input
            type="number"
            id="stopLossPercentage"
            v-model.number="stopLoss"
            step="0.01"
            min="0"
            max="1"
            @input="validateStopLossPercentage"
            required
          />
          <p class="help-text">{{ t('simulation.create.sections.parameters.stopLoss.help') }}</p>
          <p v-if="formErrors.stopLossPercentage" class="validation-error">{{ formErrors.stopLossPercentage }}</p>
        </div>

        <div class="form-group">
          <label>{{ t('simulation.create.sections.parameters.direction.label') }}</label>
          <div class="radio-group">
            <label v-for="option in directionOptions" :key="option.value">
              <input type="radio" v-model="direction" :value="option.value" />
              {{ option.label }}
            </label>
          </div>
        </div>

        <div class="form-group">
          <h4>{{ t('simulation.create.sections.parameters.quantity.title') }}</h4>
          <p class="static-text">{{ t('simulation.create.sections.parameters.quantity.description') }}</p>
        </div>
      </section>

      <section class="simulation-section">
        <h2>{{ t('simulation.create.sections.collection.title') }}</h2>
        <div v-if="isLoadingCollections" class="loading-state">
          <p>{{ t('simulation.create.sections.collection.loading') }} <span class="spinner-inline"></span></p>
        </div>
        <div v-else-if="fetchCollectionsError" class="error-state">
          <p class="error-message">{{ fetchCollectionsError }}</p>
          <button @click="fetchAvailableCollections" type="button" class="btn btn-secondary">
            {{ t('common.retry') }}
          </button>
        </div>
        <div v-else-if="availableCollections.length === 0" class="empty-state">
          <p>{{ t('simulation.create.sections.collection.empty') }}</p>
          <router-link to="/collections" class="btn btn-primary">
            {{ t('simulation.create.sections.collection.createLink') }}
          </router-link>
        </div>
        <div v-else class="options-grid">
          <div v-for="collection in availableCollections" :key="collection.id" class="option-card collection-card">
            <label>
              <input type="radio" :value="collection.id" v-model="selectedCollectionId" />
              <strong>{{ collection.name }}</strong>
              <span class="collection-meta">
                {{ t('simulation.create.sections.collection.assetCount', { count: collection.assetCount }) }}
              </span>
            </label>
          </div>
        </div>
        <p v-if="formErrors.selectedCollection" class="validation-error">{{ formErrors.selectedCollection }}</p>
        <button
          type="button"
          @click="goToCollections"
          class="btn btn-link manage-link"
          v-if="!isLoadingCollections && !fetchCollectionsError"
        >
          {{ t('simulation.create.sections.collection.manageLink') }}
        </button>
      </section>

      <section class="simulation-section">
        <h2>{{ t('simulation.create.sections.strategy.title') }}</h2>
        <div v-if="isLoadingStrategies" class="loading-state">
          <p>{{ t('simulation.create.sections.strategy.loading') }} <span class="spinner-inline"></span></p>
        </div>
        <div v-else-if="fetchStrategiesError" class="error-state">
          <p class="error-message">{{ fetchStrategiesError }}</p>
          <button @click="fetchAvailableStrategies" type="button" class="btn btn-secondary">
            {{ t('common.retry') }}
          </button>
        </div>
        <div v-else-if="availableStrategies.length === 0" class="empty-state">
          <p>{{ t('simulation.create.sections.strategy.empty') }}</p>
        </div>
        <div v-else class="options-grid strategy-grid">
          <div v-for="strategy in availableStrategies" :key="strategy.id" class="option-card strategy-card">
            <label>
              <input type="radio" :value="strategy.id" v-model="selectedStrategyId" />
              <div class="strategy-header">
                <strong>{{ strategy.name }}</strong>
              </div>
              <p class="strategy-description">{{ strategy.tagline }}</p>
              <button type="button" @click="openStrategyDetailsModal(strategy)" class="btn btn-sm btn-outline-info strategy-details-btn">
                {{ t('simulation.create.sections.strategy.detailsButton') }}
              </button>
            </label>
          </div>
        </div>
        <p v-if="formErrors.selectedStrategy" class="validation-error">{{ formErrors.selectedStrategy }}</p>
      </section>

      <div v-if="error" class="error-message global-error">{{ error }}</div>
      <p v-if="formErrors.settingsName" class="validation-error global-error">{{ formErrors.settingsName }}</p>

      <div class="actions">
        <button type="button" @click="handleSaveSettings" :disabled="isSubmitting" class="btn btn-secondary">
          {{ isSubmitting ? t('common.saving') : t('simulation.create.actions.saveSettings') }}
        </button>
        <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
          {{ isSubmitting ? t('simulation.create.actions.starting') : t('simulation.create.actions.startSimulation') }}
        </button>
      </div>
    </form>

    <BaseModal
      :isVisible="showStrategyModal"
      @close="closeStrategyDetailsModal"
      :title="currentStrategyDetails ? t('simulation.create.strategyModal.title', { name: currentStrategyDetails.name }) : t('simulation.create.strategyModal.titleFallback')"
    >
      <div v-if="currentStrategyDetails" class="strategy-modal-content">
        <p><strong>{{ t('simulation.create.strategyModal.taglineLabel') }}</strong> {{ currentStrategyDetails.tagline }}</p>
        <p><strong>{{ t('simulation.create.strategyModal.descriptionLabel') }}</strong> {{ currentStrategyDetails.description }}</p>
        <h4>{{ t('simulation.create.strategyModal.performanceTitle') }}</h4>
        <ul class="performance-list">
          <li>
            {{ t('simulation.create.strategyModal.metrics.transactions', {
              value: currentStrategyDetails.performance?.transactions ?? t('common.notAvailable')
            }) }}
          </li>
          <li>
            {{ t('simulation.create.strategyModal.metrics.winRate', {
              value: typeof currentStrategyDetails.performance?.winRate === 'number'
                ? formatPercentage(currentStrategyDetails.performance.winRate, 1)
                : t('common.notAvailable')
            }) }}
          </li>
          <li>
            {{ t('simulation.create.strategyModal.metrics.avgHolding', {
              value: typeof currentStrategyDetails.performance?.avgHoldingDays === 'number'
                ? formatDecimal(currentStrategyDetails.performance.avgHoldingDays, 0, 0)
                : t('common.notAvailable')
            }) }}
          </li>
        </ul>
        <div class="performance-chart-placeholder">
          <strong>{{ t('simulation.create.strategyModal.cumulativeTitle') }}</strong>
          <div v-if="currentStrategyDetails.performance?.cumulativePerformanceData?.length" class="chart-wrapper-modal">
            <DataChart
              type="line"
              :data="buildCumulativeChartData(currentStrategyDetails.performance.cumulativePerformanceData)"
              :options="strategyChartOptions"
            />
          </div>
          <p v-else>{{ t('simulation.create.strategyModal.noChartData') }}</p>
        </div>
        <p class="disclaimer">
          <strong>{{ t('simulation.create.strategyModal.disclaimerLabel') }}</strong>
          {{ currentStrategyDetails.disclaimer }}
        </p>
      </div>
    </BaseModal>
  </div>
</template>




<script setup>
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import BaseModal from '@/components/BaseModal.vue';
import DataChart from '@/components/DataChart.vue';
import { formatDate } from '@/utils/date.js';
import {
  getCollections as apiGetCollections,
  getStrategies as apiGetStrategies,
  saveSimulationConfig as apiSaveSimulationConfig,
  getSimulationConfigForEdit as apiGetSimulationConfigForEdit
} from '@/services/api.js';
import { useDateInputLang } from '@/composables/useDateInputLang.js';

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const { dateInputLang } = useDateInputLang();

const simulationNameFromHistory = ref('');
const isLoadingSettings = ref(false);

const tradingCost = ref(0.001);
const startDate = ref('');
const endDate = ref('');
const stopLoss = ref(0.1);
const direction = ref('long');

const formErrors = ref({
  tradingCost: '',
  startDate: '',
  endDate: '',
  stopLossPercentage: '',
  selectedCollection: '',
  selectedStrategy: '',
  settingsName: '',
  simulationName: ''
});

const availableCollections = ref([]);
const selectedCollectionId = ref(null);
const isLoadingCollections = ref(false);
const fetchCollectionsError = ref(null);

const availableStrategies = ref([]);
const selectedStrategyId = ref(null);
const isLoadingStrategies = ref(false);
const fetchStrategiesError = ref(null);
const showStrategyModal = ref(false);
const currentStrategyDetails = ref(null);

const isSubmitting = ref(false);
const error = ref(null);

const QUICK_DATE_PRESETS = Object.freeze({
  '1m': (today) => {
    const start = new Date(today);
    start.setMonth(start.getMonth() - 1);
    return start;
  },
  '6m': (today) => {
    const start = new Date(today);
    start.setMonth(start.getMonth() - 6);
    return start;
  },
  '1y': (today) => {
    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 1);
    return start;
  },
  '3y': (today) => {
    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 3);
    return start;
  },
  ytd: (today) => new Date(today.getFullYear(), 0, 1)
});

const quickDateOptions = computed(() => [
  { key: '1m', label: t('simulation.create.sections.parameters.dateRange.quick.oneMonth') },
  { key: '6m', label: t('simulation.create.sections.parameters.dateRange.quick.sixMonths') },
  { key: '1y', label: t('simulation.create.sections.parameters.dateRange.quick.oneYear') },
  { key: '3y', label: t('simulation.create.sections.parameters.dateRange.quick.threeYears') },
  { key: 'ytd', label: t('simulation.create.sections.parameters.dateRange.quick.yearToDate') }
]);

const directionOptions = computed(() => [
  { value: 'long', label: t('simulation.create.sections.parameters.direction.options.long') },
  { value: 'short', label: t('simulation.create.sections.parameters.direction.options.short') }
]);

const loadedConfigBanner = computed(() =>
  simulationNameFromHistory.value
    ? t('simulation.create.heading.loadedBanner', { name: simulationNameFromHistory.value })
    : ''
);

const formatDecimal = (value, minimumFractionDigits = 2, maximumFractionDigits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return t('common.notAvailable');
  }
  return new Intl.NumberFormat(locale.value, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(numeric);
};

const formatPercentage = (value, fractionDigits = 2) => {
  if (typeof value !== 'number') {
    return t('common.notAvailable');
  }
  return new Intl.NumberFormat(locale.value, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(value);
};

const resolveErrorMessage = (err, fallbackKey) =>
  err?.message || (fallbackKey ? t(fallbackKey) : t('common.unknownError'));

const strategyChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: { y: { beginAtZero: false } }
};

const buildCumulativeChartData = (dataPoints = []) => ({
  labels: dataPoints.map((_, index) => index + 1),
  datasets: [
    {
      label: t('simulation.create.strategyModal.chartLabel'),
      data: dataPoints,
      borderColor: '#42b983',
      backgroundColor: 'rgba(66, 185, 131, 0.1)',
      fill: true,
      tension: 0.1
    }
  ]
});

function setDefaultDates() {
  const today = new Date();
  endDate.value = formatDate(today);
  const defaultStartDate = new Date(today);
  defaultStartDate.setFullYear(today.getFullYear() - 2);
  startDate.value = formatDate(defaultStartDate);
}

function setQuickDate(presetKey) {
  const today = new Date();
  const resolver = QUICK_DATE_PRESETS[presetKey];
  if (!resolver) {
    return;
  }
  const start = resolver(today);
  startDate.value = formatDate(start);
  endDate.value = formatDate(today);
  validateDateRange();
}

async function fetchAvailableCollections() {
  isLoadingCollections.value = true;
  fetchCollectionsError.value = null;
  try {
    availableCollections.value = await apiGetCollections();
  } catch (err) {
    fetchCollectionsError.value = resolveErrorMessage(err, 'simulation.create.sections.collection.errorFallback');
  } finally {
    isLoadingCollections.value = false;
  }
}

async function fetchAvailableStrategies() {
  isLoadingStrategies.value = true;
  fetchStrategiesError.value = null;
  try {
    availableStrategies.value = await apiGetStrategies();
  } catch (err) {
    fetchStrategiesError.value = resolveErrorMessage(err, 'simulation.create.sections.strategy.errorFallback');
  } finally {
    isLoadingStrategies.value = false;
  }
}

function openStrategyDetailsModal(strategy) {
  currentStrategyDetails.value = strategy;
  showStrategyModal.value = true;
}

function closeStrategyDetailsModal() {
  showStrategyModal.value = false;
}

function goToCollections() {
  router.push('/collections');
}

function validateTradingCost() {
  const cost = Number(tradingCost.value);
  if (Number.isNaN(cost) || cost < 0 || cost > 0.1) {
    formErrors.value.tradingCost = t('simulation.create.validation.tradingCostRange');
    return false;
  }
  formErrors.value.tradingCost = '';
  return true;
}

function validateDateRange() {
  let isValid = true;
  formErrors.value.startDate = '';
  formErrors.value.endDate = '';
  if (!startDate.value) {
    formErrors.value.startDate = t('simulation.create.validation.startDateRequired');
    isValid = false;
  }
  if (!endDate.value) {
    formErrors.value.endDate = t('simulation.create.validation.endDateRequired');
    isValid = false;
  }
  if (startDate.value && endDate.value && new Date(endDate.value) < new Date(startDate.value)) {
    formErrors.value.endDate = t('simulation.create.validation.endDateAfter');
    isValid = false;
  }
  return isValid;
}

function validateStopLossPercentage() {
  const val = Number(stopLoss.value);
  if (stopLoss.value === '' || stopLoss.value === null || typeof stopLoss.value === 'undefined' || Number.isNaN(val)) {
    formErrors.value.stopLossPercentage = t('simulation.create.validation.stopLossRequired');
    return false;
  }
  if (val <= 0 || val > 1) {
    formErrors.value.stopLossPercentage = t('simulation.create.validation.stopLossRange');
    return false;
  }
  formErrors.value.stopLossPercentage = '';
  return true;
}

function validateSelectedCollection() {
  if (!selectedCollectionId.value) {
    formErrors.value.selectedCollection = t('simulation.create.validation.collectionRequired');
    return false;
  }
  formErrors.value.selectedCollection = '';
  return true;
}

function validateSelectedStrategy() {
  if (!selectedStrategyId.value) {
    formErrors.value.selectedStrategy = t('simulation.create.validation.strategyRequired');
    return false;
  }
  formErrors.value.selectedStrategy = '';
  return true;
}

function runAllValidations() {
  const isTradingCostValid = validateTradingCost();
  const isDateRangeValid = validateDateRange();
  const isStopLossValid = validateStopLossPercentage();
  const isCollectionValid = validateSelectedCollection();
  const isStrategyValid = validateSelectedStrategy();
  return isTradingCostValid && isDateRangeValid && isStopLossValid && isCollectionValid && isStrategyValid;
}

function buildSimulationPayload(name, status) {
  const strategyDetails = availableStrategies.value.find((s) => s.id === selectedStrategyId.value);
  const collectionDetails = availableCollections.value.find((c) => c.id === selectedCollectionId.value);

  return {
    name: name.trim(),
    parameters: {
      tradingCost: tradingCost.value,
      startDate: startDate.value,
      endDate: endDate.value,
      stopLoss: stopLoss.value,
      direction: direction.value
    },
    collectionId: selectedCollectionId.value,
    strategyId: selectedStrategyId.value,
    collectionName: collectionDetails?.name,
    assetCount: collectionDetails?.assetCount,
    strategyName: strategyDetails?.name,
    status
  };
}

async function handleSaveSettings() {
  error.value = null;
  formErrors.value.settingsName = '';

  if (!runAllValidations()) {
    error.value = t('simulation.create.validation.fixErrors');
    return;
  }

  const settingsName = window.prompt(t('simulation.create.prompts.saveSettings'));
  if (settingsName === null) return;

  if (!settingsName || settingsName.trim() === '') {
    formErrors.value.settingsName = t('simulation.create.validation.settingsNameRequired');
    error.value = t('simulation.create.validation.settingsNameInvalid');
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = buildSimulationPayload(settingsName, 'Draft');
    const response = await apiSaveSimulationConfig(payload);
    if (response.success && response.simulation) {
      window.alert(t('simulation.create.alerts.settingsSaved', { name: response.simulation.name }));
      router.push('/simulation/history');
    } else {
      throw new Error(response.message || t('simulation.create.errors.saveSettings'));
    }
  } catch (err) {
    console.error('Failed to save settings:', err);
    error.value = resolveErrorMessage(err, 'simulation.create.errors.saveSettings');
  } finally {
    isSubmitting.value = false;
  }
}

async function startSimulation() {
  error.value = null;
  formErrors.value.simulationName = '';

  if (!runAllValidations()) {
    error.value = t('simulation.create.validation.fixErrors');
    return;
  }

  const simulationName = window.prompt(t('simulation.create.prompts.simulationName'));
  if (simulationName === null) return;

  if (!simulationName || simulationName.trim() === '') {
    formErrors.value.simulationName = t('simulation.create.validation.simulationNameRequired');
    error.value = t('simulation.create.validation.simulationNameInvalid');
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = buildSimulationPayload(simulationName, undefined);
    const response = await apiSaveSimulationConfig(payload);
    if (response.success && response.simulation) {
      window.alert(t('simulation.create.alerts.simulationQueued', { name: response.simulation.name }));
      router.push('/simulation/history');
    } else {
      throw new Error(response.message || t('simulation.create.errors.saveSimulation'));
    }
  } catch (err) {
    console.error('Failed to save simulation:', err);
    error.value = resolveErrorMessage(err, 'simulation.create.errors.saveSimulation');
  } finally {
    isSubmitting.value = false;
  }
}

async function loadConfigFromHistory(historyId) {
  isLoadingSettings.value = true;
  error.value = null;
  simulationNameFromHistory.value = '';

  try {
    const loadedConfig = await apiGetSimulationConfigForEdit(historyId);
    if (!loadedConfig || !loadedConfig.parameters) {
      throw new Error(t('simulation.create.errors.invalidConfig'));
    }

    tradingCost.value = loadedConfig.parameters.tradingCost;
    startDate.value = loadedConfig.parameters.startDate;
    endDate.value = loadedConfig.parameters.endDate;
    stopLoss.value = loadedConfig.parameters.stopLoss;
    direction.value = loadedConfig.parameters.direction;

    if (!availableCollections.value.length && !isLoadingCollections.value) {
      await fetchAvailableCollections();
    }
    if (!availableStrategies.value.length && !isLoadingStrategies.value) {
      await fetchAvailableStrategies();
    }

    selectedCollectionId.value = loadedConfig.collectionId;
    selectedStrategyId.value = loadedConfig.strategyId;
    simulationNameFromHistory.value = loadedConfig.name;

    runAllValidations();
    window.alert(t('simulation.create.alerts.loadedFromHistory', { name: loadedConfig.name }));

    const rest = { ...route.query };
    delete rest.fromHistoryId;
    router.replace({ query: rest });
  } catch (err) {
    console.error('Failed to load history configuration:', err);
    error.value = t('simulation.create.errors.loadHistory', { message: resolveErrorMessage(err) });
  } finally {
    isLoadingSettings.value = false;
  }
}

function applyPreselectedCollection(collectionId) {
  if (!collectionId) return;
  const attempt = () => {
    if (availableCollections.value.length > 0) {
      const collection = availableCollections.value.find((c) => c.id === collectionId);
      if (collection) {
        selectedCollectionId.value = collectionId;
        validateSelectedCollection();
      }
    } else if (!isLoadingCollections.value) {
      setTimeout(attempt, 100);
    }
  };
  attempt();
}

function applyPreselectedStrategy(strategyId) {
  if (!strategyId) return;
  const attempt = () => {
    if (availableStrategies.value.length > 0) {
      const strategy = availableStrategies.value.find((s) => s.id === strategyId);
      if (strategy) {
        selectedStrategyId.value = strategyId;
        validateSelectedStrategy();
      }
    } else if (!isLoadingStrategies.value) {
      setTimeout(attempt, 100);
    }
  };
  attempt();
}

onMounted(() => {
  setDefaultDates();
  validateTradingCost();
  validateDateRange();
  validateStopLossPercentage();
  fetchAvailableCollections();
  fetchAvailableStrategies();

  const historyId = route.query.fromHistoryId;
  if (historyId) {
    loadConfigFromHistory(historyId);
  }

  applyPreselectedCollection(route.query.preselectedCollection);
  applyPreselectedStrategy(route.query.preselectedStrategy);
});

watchEffect(() => {
  const historyId = route.query.fromHistoryId;
  if (!historyId || isLoadingSettings.value) {
    return;
  }
  if (router.currentRoute.value.name === 'CreateSimulation') {
    loadConfigFromHistory(historyId);
  }
});
</script>



<style scoped>
.loading-overlay {
  position: fixed; /* Use fixed to cover the whole screen */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.85); /* Slightly more opaque */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* Ensure it's on top */
}
.loading-overlay p {
  font-size: 1.2em;
  margin-top: 15px; /* Space between spinner and text */
  color: #333;
}

.loaded-from-history-banner {
  background-color: #d1ecf1; /* Light info blue background */
  border: 1px solid #bee5eb; /* Info blue border */
  color: #0c5460; /* Dark info blue text */
  padding: 15px 20px;
  margin-bottom: 25px;
  border-radius: 6px;
  text-align: center;
  font-size: 1.05em; /* Slightly larger font */
  line-height: 1.6;
}
.loaded-from-history-banner strong {
  font-weight: 600; /* Bolder name */
}


.create-simulation-page {
  padding: 20px;
  max-width: 800px;
  margin: 20px auto; /* Centering and margin */
  font-family: Arial, sans-serif;
  background-color: #fff; /* Page background */
  border-radius: 8px; /* Rounded corners for the page */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 10px;
}

.create-simulation-page > p { /* Targeting the main descriptive paragraph */
  text-align: center;
  color: #666;
  margin-bottom: 30px;
  font-size: 1.1em;
}

.simulation-section {
  margin-bottom: 30px;
  padding: 25px; /* Increased padding */
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fdfdfd; /* Slightly off-white section background */
}

.simulation-section h2 {
  margin-top: 0;
  margin-bottom: 20px; /* Increased space */
  color: #0056b3; /* Theme color for section titles */
  border-bottom: 2px solid #007bff; /* Themed border */
  padding-bottom: 10px;
  font-size: 1.6em; /* Larger section titles */
}

.form-group {
  margin-bottom: 20px; /* Consistent spacing */
}
.form-group label,
.form-group h4 {
  display: block;
  margin-bottom: 8px; /* Space after label */
  color: #495057; /* Darker label text */
  font-weight: bold;
  font-size: 1em;
}
.form-group input[type="text"], /* Added for simulationName */
.form-group input[type="number"],
.form-group input[type="date"],
.form-group select { /* Added select styling */
  width: 100%;
  padding: 10px 12px; /* Comfortable padding */
  border: 1px solid #ced4da; /* Standard border color */
  border-radius: 4px;
  box-sizing: border-box; /* Important for width 100% */
  font-size: 1em;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.form-group input:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.help-text {
  font-size: 0.85em;
  color: #6c757d; /* Muted help text */
  margin-top: 5px;
}
.static-text { /* For non-input descriptive text within form-group like structure */
    font-size: 0.95em;
    color: #333;
    padding: 10px 0;
}

.validation-error {
  color: #dc3545;
  font-size: 0.85em;
  margin-top: 5px;
}
.global-error { /* For general form errors */
  text-align: center;
  padding: 10px;
  margin-bottom:15px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  border-radius: 4px;
}

.date-range-group {
  display: flex;
  gap: 20px; /* Space between date inputs */
}
.date-range-group > div {
  flex: 1; /* Each date input takes equal space */
}
.quick-date-buttons {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.quick-date-buttons button {
    margin-right: 0; /* Remove individual margin if gap is used */
}


.radio-group label {
  margin-right: 20px;
  font-weight: normal; /* Normal weight for radio labels */
}
.radio-group input[type="radio"] {
  margin-right: 5px;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}
.option-card {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  transition: box-shadow 0.2s ease;
}
.option-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.option-card label {
  display: block; /* Make label take full width */
  font-weight: normal;
  cursor: pointer;
}
.option-card label strong {
  display: block;
  margin-bottom: 5px;
  color: #333;
}
.strategy-card label {
  display: flex; /* Align radio and text */
  align-items: center;
}
.strategy-card input[type="radio"] {
  margin-right: 10px;
}
.strategy-tagline, .strategy-desc { /* For strategy card */
    font-size: 0.9em;
    color: #555;
    margin-left: 25px; /* Indent below radio */
    display: block;
}
.strategy-details-btn {
  margin-left: auto; /* Push to the right */
  font-size: 0.85em;
  padding: 3px 8px;
}
.full-width-error { /* For errors that should span across grid columns */
  grid-column: 1 / -1;
  text-align: center;
}


.selected-info {
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9em;
}
.alert-info { /* Bootstrap-like info alert */
  color: #0c5460;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.manage-link {
    margin-top: 10px;
    display: inline-block;
}

.actions {
  margin-top: 30px;
  text-align: center;
  display: flex;
  gap: 15px; /* Space between buttons */
  justify-content: center; /* Center buttons */
}

.btn {
  padding: 12px 25px; /* Larger buttons */
  border-radius: 5px;
  cursor: pointer;
  font-size: 1.1em; /* Larger font */
  border: none;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.btn-primary {
  background-color: #007bff;
  color: white;
}
.btn-primary:hover {
  background-color: #0069d9;
  box-shadow: 0 2px 5px rgba(0,123,255,0.3);
}
.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  box-shadow: none;
}
.btn-secondary { /* Style for Save Settings */
  background-color: #6c757d;
  color: white;
}
.btn-secondary:hover {
  background-color: #5a6268;
  box-shadow: 0 2px 5px rgba(108,117,125,0.3);
}
.btn-secondary:disabled {
  background-color: #9fa7ad;
  cursor: not-allowed;
  box-shadow: none;
}


.spinner-inline {
  width: 1em;
  height: 1em;
  border: 0.15em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: spin-inline 0.75s linear infinite; /* Renamed animation */
  vertical-align: text-bottom;
  margin-left: 5px;
}
/* General spinner for loading overlay */
.spinner {
  border: 6px solid rgba(0, 0, 0, 0.1);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border-left-color: #007bff; /* Theme color */
  animation: spin-main 1s ease infinite; /* Renamed animation */
}

@keyframes spin-inline { /* Renamed */
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes spin-main { /* Renamed */
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state, .error-state, .empty-state { /* For sections */
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}
.error-state .error-message { /* For section specific errors */
    color: #721c24;
    background-color: #f8d7da;
    padding: 10px;
    border-radius: 4px;
}

.strategy-modal-content {
  padding: 10px;
}
.strategy-modal-content h3 {
  margin-top: 0;
  color: #0056b3;
}
.performance-list {
  list-style: disc;
  padding-left: 20px;
}
.performance-chart-placeholder {
  margin-top: 15px;
  padding: 10px;
  background-color: #f0f0f0;
  border-radius: 4px;
  text-align: center;
}
.chart-wrapper-modal {
  height: 250px; /* Or any desired height for modal chart */
  margin-top:10px;
}
.disclaimer {
  font-size: 0.8em;
  color: #888;
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 10px;
}
</style>
